'use client'

import { ReactNode, useEffect, useState } from 'react'
import { Button, Cell, Checkbox, Dialog, Spacer, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import { clickEvent } from '@marketplace-web/personal-information/personal-information-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'

import { PersonalInformationSharingContentType } from '../../constants/personal-information-sharing'

import PersonalInformationSharingWarning from './PersonalInformationSharingWarning'

type Props = {
  threadId: string
  contentType: PersonalInformationSharingContentType
  onSetPersonalInformationSharingContentType: (
    contentType: PersonalInformationSharingContentType | null,
  ) => void
  onSendClick: () => void
}

const PersonalInformationSharingModal = ({
  threadId,
  contentType,
  onSetPersonalInformationSharingContentType,
  onSendClick,
}: Props) => {
  const [isAgreementChecked, setIsAgreementChecked] = useState(false)

  const { track } = useTracking()

  const translate = useTranslate('conversation.personal_information_sharing_dialog')

  useEffect(() => {
    track(
      clickEvent({
        screen: 'message_reply',
        target: 'personal_information_modal_show',
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )
  }, [track, contentType, threadId])

  const handleAgreementChange = () => {
    setIsAgreementChecked(prevState => !prevState)
  }

  const handleLearnMoreClick = () => {
    track(
      clickEvent({
        screen: 'message_reply',
        target: 'personal_information_modal_learn_more',
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )
  }

  const handleSendClick = () => {
    track(
      clickEvent({
        screen: 'message_reply',
        target: 'personal_information_modal_send_anyway',
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )

    onSendClick()
    onSetPersonalInformationSharingContentType(null)
  }

  const handleEditClick = () => {
    track(
      clickEvent({
        screen: 'message_reply',
        target: 'personal_information_modal_change_response',
        targetDetails: JSON.stringify({
          thread_id: threadId,
          content_type: contentType,
        }),
      }),
    )

    onSetPersonalInformationSharingContentType(null)
  }

  const phishingExamplesLink = {
    'phishing-examples-link': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="phishing-examples-link"
        type={FaqEntryType.SpamPhishing}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  return (
    <Dialog contentDimensions={{ maxWidth: '343px' }} testId="email-sharing-modal" show>
      <Cell>
        <Text
          as="h2"
          type={Text.Type.Heading}
          text={
            contentType === PersonalInformationSharingContentType.Email
              ? translate('email.title')
              : translate('phone.title')
          }
          alignment={Text.Alignment.Center}
        />
        <Spacer size={Spacer.Size.Large} />
        <PersonalInformationSharingWarning contentType={contentType} />
        <Spacer size={Spacer.Size.Large} />
        <Text as="p" text={translate('protect_your_data', phishingExamplesLink)} />
        <Spacer size={Spacer.Size.XLarge} />
        <Checkbox
          name="agreement-checkbox"
          text={
            contentType === PersonalInformationSharingContentType.Email
              ? translate('email.agreement_label')
              : translate('phone.agreement_label')
          }
          onChange={handleAgreementChange}
          checked={isAgreementChecked}
        />
        <Spacer size={Spacer.Size.XLarge} />
        <Button
          text={translate('actions.edit_message')}
          styling={Button.Styling.Filled}
          onClick={handleEditClick}
          testId="email-sharing-modal--edit-button"
        />
        <Spacer size={Spacer.Size.Large} />
        <Button
          text={translate('actions.send')}
          styling={Button.Styling.Flat}
          onClick={handleSendClick}
          disabled={!isAgreementChecked}
          testId="email-sharing-modal--send-button"
        />
      </Cell>
    </Dialog>
  )
}

export default PersonalInformationSharingModal
